import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import Toaster from "../../components/ui/toaster";
import { Loader2 } from "lucide-react";
import OtpInput from "react-otp-input";
import { Button } from "../../components/ui/button";
import twoFaIcon from "../../assets/images/settings/2faGraphic.svg";
import logo from "../../assets/images/80eightLogo.png";
import { create, getQuery, setAuthorization } from "../../services/apiService";
import { useAuth } from "../../context/AuthContext";
import { setAuthToken, setAuthData } from "../../context/AuthActions";

export default function LoginTwoFA() {
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { updateProfile, fetchUserSettings } = useAuth();

  useEffect(() => {
    // If no pendingId, redirect to login (only check on mount)
    const pendingId = localStorage.getItem("pendingId");
    if (!pendingId) {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (otp) => {
    setOtp(otp);
  };

  const handleCancel = () => {
    localStorage.removeItem("pendingId");
    navigate("/login");
  };

  const verify2FALogin = async () => {
    if (otp.length !== 6) {
      toast({
        description: "Please enter a valid 6-digit code",
        variant: "error",
      });
      return;
    }

    // Get pendingId from localStorage
    const pendingId = localStorage.getItem("pendingId");
    if (!pendingId) {
      toast({
        description: "Session expired. Please login again.",
        variant: "error",
      });
      navigate("/login");
      return;
    }

    setIsLoading(true);
    try {
      const response = await create("/api/v1/auth/verify-2fa-login", {
        pendingId: pendingId,
        otpCode: otp,
      });

      if (response.status === 200 && response.data.status) {
        const { access_token } = response.data.data;

        // Clear pendingId
        localStorage.removeItem("pendingId");

        // Store token
        setAuthToken(access_token);
        setAuthorization(access_token);

        // Get full user account
        const userResponse = await getQuery("/api/v1/user/get-account");

        if (userResponse.status === 200 && userResponse.data.data) {
          const userData = userResponse.data.data;

          // Build session object
          const session = {
            aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
            email: userData.email,
            emailVerified: userData.emailVerified,
            name: `${userData.personal_info?.name || ""} ${
              userData.personal_info?.lastname || ""
            }`.trim(),
            updatedAt: userData.updatedAt,
            sub: userData.authId || userData.userId,
          };

          // Store all auth data
          setAuthData({
            token: access_token,
            userId: userData.authId || userData.userId,
            email: userData.email,
            session: session,
            accountId: userData.accountId,
          });

          updateProfile(userData);

          // Fetch user settings after successful 2FA login
          if (userData.profile_completed) {
            await fetchUserSettings(userData, access_token);
          }

          toast({
            description: "2FA verification successful!",
            variant: "success",
          });

          // Navigate based on user state
          if (!userData.emailVerified) {
            navigate("/onboarding/verify-email");
          } else if (!userData.profile_completed) {
            navigate("/complete-profile");
          } else {
            navigate("/");
          }
        }
      } else {
        toast({
          description: "Invalid 2FA code",
          variant: "error",
        });
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      const errorMessage = error.response?.data?.message || "Invalid 2FA code";
      toast({
        description: errorMessage,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={logo} alt="80Eight Logo" className="h-12" />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-8 py-10">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <img src={twoFaIcon} alt="2FA" className="h-24 w-24" />
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                Enter your 2FA code
              </h1>
              <p className="text-gray-600 text-base">
                Please enter your 6-digit authentication code from your
                authenticator app.
              </p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center mb-8">
              <OtpInput
                value={otp}
                onChange={handleChange}
                numInputs={6}
                separator={<span className="w-2" />}
                shouldAutoFocus
                renderInput={(props) => (
                  <input
                    {...props}
                    className="!w-12 !h-12 text-xl font-bold text-center border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-400 hover:border-purple-300 transition-all outline-none bg-white"
                  />
                )}
                inputStyle={{
                  width: "48px",
                  height: "48px",
                  margin: "0 4px",
                  fontSize: "20px",
                  borderRadius: "8px",
                  border: "2px solid #d1d5db",
                  textAlign: "center",
                }}
                focusStyle={{
                  border: "2px solid #a855f7",
                  outline: "none",
                }}
              />
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <Button
                onClick={verify2FALogin}
                disabled={isLoading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 text-base font-semibold"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin text-white h-5 w-5" />
                ) : (
                  "Verify 2FA"
                )}
              </Button>

              <Button
                onClick={handleCancel}
                variant="outline"
                className="w-full border-2 border-gray-300 text-gray-700 py-4 rounded-full hover:bg-gray-50 text-base font-medium"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-400">
          <p>
            Can't access your authenticator?{" "}
            <button
              onClick={() => navigate("/support/index")}
              className="text-purple-400 font-semibold hover:text-purple-300 transition-colors"
            >
              Contact support
            </button>
          </p>
        </div>
      </div>
      <Toaster />
    </div>
  );
}
